<template>
    <div class="VideoUploadView" :class="{
        ...modes.map((m) => `VideoUploadView--${m}`),
        'VideoUploadView--withRemove': showRemove,
        'u-ErrorView': hasError,
        'VideoUploadView--error': isError,
        isLoader: isLoader
    }">
        <div class="VideoUploadViewCaption" v-if="title">
            {{ title }}
        </div>

        <div class="VideoUploadViewInner" @dragover.prevent @drop="onDrop" :style="innerStyles">
            <label class="FullEditView VideoUploadViewLabel">
                <div class="VideoUploadViewLabelContent">
                    <span v-show="!isLoader" class="VideoUploadViewLabelText" :class="{ fileUrl: showName }">
                        <slot></slot>
                        <p v-if="fileUrl">{{ showName }}</p>
                    </span>
                    <input type="file" name="file" class="VideoUploadViewInput" @change="onSelect" />
                </div>

                <slot name="edit" v-if="hasEdit">
                    <edit-view></edit-view>
                </slot>
            </label>

            <div alt="" class="VideoUploadViewPreview"></div>
            <slot name="overlay"></slot>
            <slot name="remove" v-if="showRemove">
                <remove-view @click="onRemove"></remove-view>
            </slot>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "VideoUploadView",
    mixins: [mixins.ValidationComponentMixin],
    props: {
        tnKey: {
            type: String,
            default: ""
        },
        value: {
            type: [Object, String],
            default: () => { },
        },
        defaultImageUrl: {
            type: String,
            default: ""
        },
        modes: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: ""
        },
        caption: {
            type: String,
            default: ""
        },
        hasEdit: {
            type: Boolean,
            default: false
        },
        hasRemove: {
            type: Boolean,
            default: false
        },
        isError: {
            type: Boolean,
            default: false
        },
        isConvertToJPEG: {
            type: Boolean,
            default: false
        },
        width: {
            type: String,
            default: '100%'
        },
        height: {
            type: String,
            default: '40px'
        },
        otherStyles: {
            type: Object,
            default: () => { }
        },
        objectFit: {
            type: String,
            default: "cover"
        },
    },
    data() {
        return {
            isLoader: false,
        };
    },
    computed: {
        innerStyles() {
            return {
                width: this.width,
                height: this.height,
                ...this.otherStyles
            }
        },
        showRemove() {
            return this.hasRemove && this.innerValue;
        },
        fileUrl() {
            let url = ''
            if (this.innerValue) {
                url = this.innerValue

                if (this.innerValue.url) {
                    url = this.innerValue.url
                }
            }

            return url
        },
        showName() {
            const fileName = this.fileUrl.split("/").pop()
            const trimmedFilename = fileName.replace(/_[\w\d]+__[\w\d]+/, "");
            return trimmedFilename
        },
        isCopy() {
            return !this.fileUrl.startsWith('/');
        }
    },
    methods: {
        onCopy() {
            navigator.clipboard.writeText(this.fileUrl)
        },
        onRemove() {
            this.innerValue = null;
        },
        onDrop(e) {
            e.stopPropagation();
            e.preventDefault();
            this.takeFile(e.dataTransfer.files[0]);
        },
        onSelect(e) {
            this.takeFile(e.target.files[0]);
        },
        takeFile(file) {
            if (!file || !file.type) {
                return;
            }

            if (!file.type.startsWith("video/")) {
                alert("Select a video file");
                return;
            }

            this.uploadFile(file);
        },

        async uploadFile(file) {
            this.isLoader = true;

            const parts = file.name.split(".");
            const name = parts[0];

            let data = new FormData();
            data.append("file", file, this.isConvertToJPEG ? `${name}.tiff` : file.name);

            let config = {
                headers: { "Content-Type": "multipart/form-data" },
            };

            await axios
                .post('/control/upload_temp_file/', data, config)
                .then((response) => {
                    this.innerValue = response.data;
                })
                .catch((error) => {
                    console.log(error);
                    alert(error);
                });

            this.isLoader = false;
        },
    },
};
</script>

<style scoped lang="less">
@import "~styles/const.less";
@import "~styles/styles.less";

@r: .VideoUploadView;

@{r} {
    position: relative;
    height: fit-content;
    width: 100%;

    &.isLoader {
        &:before {
            content: ' ';
            display: block;
            .cLoader(70px);
        }
    }

    &Inner {
        position: relative;
        background-color: white;
        font-size: 12px;
        line-height: 16px;
        z-index: 100;

        &-copy {
            position: absolute;
            top: 5px;
            right: 40px;
            width: 28px;
            height: 28px;
            padding: 5px;
            z-index: 300;
            border-radius: 50%;
            border: 1px solid black;
            cursor: pointer;

            &:hover {
                opacity: .7;
            }

            >svg {
                width: 100%;
                height: 100%;
            }
        }
    }

    &Label {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
        cursor: pointer;
        transition: background-color .3s ease;

        &:hover {
            background-color: black;

            @{r}LabelText {
                color: white;
            }
        }

        &Content {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            transition: opacity 0.2s;
            cursor: pointer;
        }

        &Text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            cursor: pointer;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 16px;
            color: black;
            width: 100%;
            transition: color .3s ease;

            &.fileUrl {
                top: 0;
                left: 15px;
                transform: none;
                width: fit-content;
            }
        }
    }

    &Input {
        display: none;
    }

    &Preview {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-position: center;
        background-repeat: no-repeat;
        border: 1px solid black;
        display: flex;
        align-items: center;
        padding-left: 10px;
        font-family: @Graphik-LCG;
    }

    &Caption {
        display: inline-block;
        font-size: 12px;
        line-height: 16px;
        color: black;
        margin-bottom: 10px;
    }

    &--withRemove {
        .RemoveView {
            top: 9px;
            left: auto;
            right: 15px;

            :deep(.RemoveViewButton) {
                width: 20px;
                height: 20px;
            }
        }

        :deep(.RemoveViewButton) {
            width: 28px;
            height: 28px;
        }

        .EditView {
            right: 60px !important;
        }
    }

    &--error {
        @{r}Label {
            border: 1px solid @color-red;
        }
    }
}

.u-ShowErrors @{r}.u-ErrorView @{r}Inner {
    border: 1px solid red;
}
</style>
